/**
 * Iter-156A.4 Phase 4 — Transfer Ownership dialog.
 *
 * Visually + semantically distinct from the generic ConfirmDialog
 * because the consequences are unique:
 *
 *   - The current Owner permanently becomes Administrator.
 *   - The selected member permanently becomes Owner.
 *   - Project always retains exactly one Owner.
 *
 * The backend additionally requires an explicit `confirm: "transfer"`
 * string in the request body — see /api/projects/{id}/owner/transfer.
 * We surface that as a TYPE-TO-CONFIRM gate so users cannot fat-finger
 * the action.
 */
import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ShieldCheck, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";

export default function TransferOwnershipDialog({
    open,
    onOpenChange,
    members = [],
    onConfirm, // (user_id) => Promise<void>
    busy = false,
}) {
    const [targetId, setTargetId] = useState("");
    const [typed, setTyped] = useState("");

    useEffect(() => {
        if (!open) {
            setTargetId("");
            setTyped("");
        }
    }, [open]);

    // Owner row is filtered out — you can't transfer to yourself.
    const candidates = (members || []).filter((m) => m.role !== "owner");
    const target = candidates.find((m) => m.user_id === targetId);
    const typedOk = typed.trim().toLowerCase() === "transfer";
    const canConfirm = Boolean(target) && typedOk && !busy;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="transfer-ownership-dialog"
                className="max-w-md"
            >
                <DialogHeader>
                    <DialogTitle
                        data-testid="transfer-ownership-title"
                        className="font-display text-xl font-bold text-foreground flex items-center gap-2"
                    >
                        <ShieldCheck size={18} className="text-amber-500" />
                        Transfer Ownership
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        Hand over the project to another member. This is a
                        deliberate action — once you transfer, you lose
                        governance control (archive, delete, transfer,
                        backup settings).
                    </DialogDescription>
                </DialogHeader>

                {/* Consequence summary — non-negotiable copy */}
                <ul
                    data-testid="transfer-ownership-consequences"
                    className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/15 p-3 text-[13px] text-amber-900 dark:text-amber-50 leading-relaxed"
                >
                    <li className="flex items-start gap-2">
                        <ArrowRight size={14} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-200" />
                        <span>
                            <span className="font-semibold">New Owner</span>{" "}
                            gains full governance control (archive, delete,
                            transfer, manage backup & security).
                        </span>
                    </li>
                    <li className="flex items-start gap-2">
                        <ArrowRight size={14} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-200" />
                        <span>
                            <span className="font-semibold">You</span> become
                            an Administrator. You keep the ability to invite,
                            manage Contributors &amp; Viewers, and edit
                            content — but not the governance actions above.
                        </span>
                    </li>
                </ul>

                {/* Target selector */}
                <div className="mt-4 space-y-2">
                    <label
                        htmlFor="transfer-target"
                        className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
                    >
                        New owner
                    </label>
                    {candidates.length === 0 ? (
                        <div
                            data-testid="transfer-ownership-no-candidates"
                            className="rounded-md border border-white/10 bg-secondary/30 p-3 text-xs text-muted-foreground"
                        >
                            No eligible members yet. Invite someone first,
                            then come back here.
                        </div>
                    ) : (
                        <select
                            id="transfer-target"
                            data-testid="transfer-ownership-target-select"
                            value={targetId}
                            onChange={(e) => setTargetId(e.target.value)}
                            disabled={busy}
                            className="w-full h-10 px-3 rounded-md text-sm bg-card border border-white/10 focus:ring-1 focus:ring-primary focus:outline-none"
                        >
                            <option value="">Choose a member…</option>
                            {candidates.map((m) => (
                                <option key={m.user_id} value={m.user_id}>
                                    {m.name || m.email}{" "}
                                    {m.role
                                        ? `· ${
                                              m.role === "admin"
                                                  ? "Administrator"
                                                  : m.role === "viewer"
                                                      ? "Viewer"
                                                      : "Contributor"
                                          }`
                                        : ""}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Type-to-confirm gate */}
                {candidates.length > 0 ? (
                    <div className="mt-3 space-y-2">
                        <label
                            htmlFor="transfer-confirm"
                            className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
                        >
                            Type <span className="font-semibold text-foreground">transfer</span> to confirm
                        </label>
                        <Input
                            id="transfer-confirm"
                            data-testid="transfer-ownership-confirm-input"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            disabled={busy}
                            placeholder="transfer"
                            autoCapitalize="off"
                            autoCorrect="off"
                        />
                    </div>
                ) : null}

                <DialogFooter className="gap-2 flex-col sm:flex-row mt-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        data-testid="transfer-ownership-cancel"
                        className="min-tap"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={() => onConfirm(target.user_id)}
                        disabled={!canConfirm}
                        data-testid="transfer-ownership-confirm"
                        className="min-tap bg-amber-500 hover:bg-amber-400 text-neutral-900 font-semibold"
                    >
                        {busy ? (
                            <>
                                <Loader2 size={14} className="mr-2 animate-spin" />
                                Transferring…
                            </>
                        ) : (
                            <>
                                <AlertTriangle size={14} className="mr-2" />
                                Transfer Ownership
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
